import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'

import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type { PostApiAdmintoolMarketsApiArg, PutApiAdmintoolMarketsByMarketIdApiArg } from '#api/tmsApiService'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import { _pushOrReplaceMarket, _setMarketData, _setMarketStatus, loadMarkets } from '#store/marketsSlice'
import { store } from '#store/root'
import type { EnhancedMarket } from '#types/EnhancedMarket'

export const marketsObserver = (() => {
  const middleware = createListenerMiddleware()
  middleware.startListening({
    matcher: isAnyOf(loadMarkets),
    effect: async (action, _listenerApi) => {
      const state = store.getState()
      if (loadMarkets.match(action)) {
        if (state.markets?.status === 'loaded' && !action.payload?.forceReload) {
          return
        }
        try {
          store.dispatch(_setMarketStatus({ status: 'loading' }))
          const request = store.dispatch(
            enhancedTmsApiService.endpoints.getApiAdmintoolMarkets.initiate({
              recursive: false,
            }),
          )
          const result = await request.unwrap()

          if (result.markets) {
            const validMarkets = result.markets.filter((m) => m.marketId !== undefined) as EnhancedMarket[]
            store.dispatch(_setMarketData(validMarkets))
            store.dispatch(_setMarketStatus({ status: 'loaded' }))
          }
        } catch (e) {
          store.dispatch(_setMarketStatus({ status: 'failed', error: makeSerializedError(e) }))
        }
      }
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.postApiAdmintoolMarkets.matchFulfilled,
    effect: (action, api) => {
      console.log('Done POST /admintool/markets', action, api)
      const originalArgs = action.meta.arg.originalArgs as PostApiAdmintoolMarketsApiArg
      api.dispatch(
        _pushOrReplaceMarket({
          marketId: originalArgs.adminNatCodesNewMarketRequest.marketId as EnhancedMarketId,
          market: originalArgs.adminNatCodesNewMarketRequest as EnhancedMarket,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiAdmintoolMarketsByMarketId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done PUT /admintool/markets', action, api)
      const originalArgs = action.meta.arg.originalArgs as PutApiAdmintoolMarketsByMarketIdApiArg
      api.dispatch(
        _pushOrReplaceMarket({
          marketId: originalArgs.marketId as EnhancedMarketId,
          market: {
            marketId: originalArgs.marketId as EnhancedMarketId,
            ...originalArgs.adminNatCodesMarketDescription,
          } as EnhancedMarket,
        }),
      )
    },
  })

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.deleteApiAdmintoolMarketsByMarketId.matchFulfilled,
    effect: (action, api) => {
      console.log('Done DELETE /admintool/markets', action, api)
      const { marketId } = action.meta.arg.originalArgs as { marketId: EnhancedMarketId }
      api.dispatch(
        _pushOrReplaceMarket({
          marketId,
          market: null,
        }),
      )
    },
  })

  return middleware
})()
